import { useMemo } from 'react';
import { format } from 'date-fns';
import type { RouteRecallDetail, RouteTrackerItem } from '@/types/routeTracking';
import type { ServiceEventCompletion } from '@/types/serviceEvents';
import { buildUpdateHistory } from '../../utils/buildUpdateHistory';
import { SectionHeader } from './SectionHeader';
import { TechDayCard, type TechDayReport } from './TechDayCard';

interface StatusUpdatesSectionProps {
  routes: RouteTrackerItem[];
  serviceEventsMap: Map<string, ServiceEventCompletion> | undefined;
  selectedDate: Date;
  onOpenWorkSummary: (recall: RouteRecallDetail) => void;
}

// BOTTOM section of the Route Tracker right panel. Renders a per-technician
// day report card for each tech with resolved (completed/released) recalls,
// plus a per-tech Daily Route Summary entry point.
export function StatusUpdatesSection({
  routes,
  serviceEventsMap,
  selectedDate,
  onOpenWorkSummary
}: StatusUpdatesSectionProps) {
  // Tech grouping derivation, memoized — routes drives ~all of it. KPI roll-ups
  // (active time, day window) come from the prefetched service_events map.
  const reports: TechDayReport[] = useMemo(() => {
    if (routes.length === 0) return [];

    const entries = buildUpdateHistory(routes);

    // One TechDayReport per technicianId (route sequences collapse into the same
    // tech card — the Daily Summary URL also collapses by tech/date so this stays
    // consistent end to end).
    const byTech = new Map<string, TechDayReport>();
    routes.forEach(route => {
      const existing = byTech.get(route.technicianId);
      if (existing) {
        existing.totalRecalls += route.totalRecalls;
        existing.completedCount += route.completedRecalls;
      } else {
        byTech.set(route.technicianId, {
          technicianId: route.technicianId,
          technicianName: route.technicianName,
          routeDate: route.routeDate,
          entries: [],
          totalRecalls: route.totalRecalls,
          completedCount: route.completedRecalls,
          activeMinutes: 0
        });
      }
    });
    entries.forEach(entry => {
      const report = byTech.get(entry.technicianId);
      if (report) report.entries.push(entry);
    });

    // KPI roll-up per tech — pull from prefetched service events, defensively
    // coercing in case a row arrives without hours_worked.
    byTech.forEach(report => {
      let earliest: string | undefined;
      let latest: string | undefined;
      let totalMinutes = 0;
      report.entries.forEach(entry => {
        const ev = serviceEventsMap?.get(entry.assignmentId);
        if (!ev) return;
        if (typeof ev.hours_worked === 'number' && Number.isFinite(ev.hours_worked)) {
          totalMinutes += ev.hours_worked * 60;
        }
        if (ev.started_at && (!earliest || ev.started_at < earliest)) earliest = ev.started_at;
        if (ev.completed_at && (!latest || ev.completed_at > latest)) latest = ev.completed_at;
      });
      report.activeMinutes = totalMinutes;
      if (earliest) report.earliestStart = earliest;
      if (latest) report.latestEnd = latest;
    });

    // Alphabetical by tech name — keeps the order stable across re-renders and
    // matches dispatcher intuition. (Sort by name, not by completion percent,
    // so the same tech doesn't jump positions as work lands.)
    return Array.from(byTech.values()).sort((a, b) =>
      a.technicianName.localeCompare(b.technicianName)
    );
  }, [routes, serviceEventsMap]);

  // Section hides entirely when there's nothing to anchor it — no routes for the
  // selected date. (For routes-but-no-resolved-items we still render the section
  // with per-tech "no resolved items yet today" hints so dispatchers know who's
  // out there.)
  if (routes.length === 0) return null;

  return (
    <section className='mt-10'>
      <SectionHeader title='Status Updates' subtitle={format(selectedDate, 'EEE, MMM d, yyyy')} />
      {reports.length === 0 ? (
        <p className='px-1 py-6 text-sm text-gray-500'>
          No completed work yet for {format(selectedDate, 'MMM d')}.
        </p>
      ) : (
        reports.map(report => (
          <TechDayCard
            key={report.technicianId}
            report={report}
            serviceEventsMap={serviceEventsMap}
            onOpenWorkSummary={onOpenWorkSummary}
          />
        ))
      )}
    </section>
  );
}
